import { accountsEndpoints, billingEndpoints as endpoints } from '@/api/endpoints';
import { brainCache } from '@/hooks/useBrains';
import { CampaignListReconciler } from '@/modules/campaigns/api';
import { reloadCollectionDocuments } from '@/modules/collections/hooks/useAPIDocuments';
import { Brain } from '@/shared/types/brain';
import { SocketEvent } from '@/shared/types/rtm';

import { bind } from './rtmBindings';

/** Account-wide events, delivered to the account room that RtmProvider subscribes to. */
export const accountBindings = (campaignListReconciler: CampaignListReconciler) => [
  bind(SocketEvent.brains_created, (data, { rtm }) => rtm.create('brains', data.brain_id)),
  bind(SocketEvent.brains_updated, (data, { rtm }) => rtm.update('brains', data.brain_id)),
  bind(SocketEvent.brains_removed, (data, { rtm }) => rtm.remove('brains', data.brain_id)),
  // Status changes come from backend processing, not from the user's own mutation.
  bind(
    SocketEvent.brains_status,
    (data, { queryClient }) => brainCache.updated(queryClient, data as Partial<Brain> as Brain),
    { includeOwn: true }
  ),

  bind(SocketEvent.desks_created, (data, { rtm }) => rtm.create('desks', data.desk_id)),
  bind(SocketEvent.desks_updated, (data, { rtm }) => rtm.update('desks', data.desk_id)),
  bind(SocketEvent.desks_removed, (data, { rtm }) => rtm.remove('desks', data.desk_id)),

  bind(SocketEvent.collections_created, (data, { rtm }) =>
    rtm.create('collections', data.collection_id)
  ),
  // Also sent once for a publish or draft, which changes documents across the collection
  bind(SocketEvent.collections_updated, (data, { rtm, queryClient }) => {
    rtm.update('collections', data.collection_id);
    reloadCollectionDocuments(queryClient, data.collection_id);
  }),
  bind(SocketEvent.collections_removed, (data, { rtm }) =>
    rtm.remove('collections', data.collection_id)
  ),

  bind(SocketEvent.campaigns_created, (data, { rtm }) => rtm.create('campaigns', data.campaign_id)),
  // Backend processing updates fields (status, counts) our own mutation response doesn't have.
  bind(
    SocketEvent.campaigns_updated,
    (data, { rtm }) => rtm.update('campaigns', data.campaign_id),
    { includeOwn: true }
  ),
  bind(SocketEvent.campaigns_removed, (data, { rtm }) => {
    rtm.remove('campaigns', data.account_id, data.campaign_id);
    // Surgery above only fixes rows this client has loaded; the debounced refetch
    // reconciles what it cannot see (off-page rows, filtered totals, page repacking).
    campaignListReconciler.schedule(data.account_id);
  }),

  // Keyed by prefix: the event is for this account, and the listener must not hold on to the
  // slug it was registered with.
  bind(
    SocketEvent.billing_subscription_deleted,
    (_data, { queryClient }) => {
      queryClient.invalidateQueries({ queryKey: [endpoints.billingState] });
      queryClient.invalidateQueries({ queryKey: [endpoints.upcomingInvoices] });
      queryClient.invalidateQueries({ queryKey: [accountsEndpoints.account] });
    },
    { includeOwn: true }
  ),
];
